import { useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, Users, User, Send, LogOut, Menu, X, Bell, Briefcase, CalendarDays, Tag, Building2, Rocket, Command, ShieldCheck, UserCheck, FileEdit, BarChart3, Handshake, ScrollText, Crown, PenLine, Inbox } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IS_CORP, AX_ADMIN_LABEL } from '@/theme/adminTheme';
import { can } from '@/utils/perms';
import { API_BASE_URL } from '@/utils/env';
import { getFreshToken } from '@/utils/auth';
import '@/styles/adminShell.css';

/* Unanswered client requests, for the Requests badge. Cached across navigations
   so moving between admin pages doesn't re-hit the backend every time. */
let openReqs = { n: 0, at: 0 };
const OPEN_REQS_TTL = 60 * 1000;
const logo = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/com_logo.png";

/* Admin nav. Accent, spacing and RTL behaviour all come from adminShell.css
   (`.ax-side*`) — it used to carry two hardcoded Tailwind palettes chosen by a
   ternary on every element, which is why its amber never matched the pages'. */

export default function AdminSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const go = (path: string) => { navigate(path); setMobileMenuOpen(false); };
  const handleLogout = () => {
    localStorage.removeItem('adminUser');
    localStorage.removeItem('user');
    localStorage.removeItem('authTokens');
    navigate('/admin-login');
  };

  const adminEmail = (() => { try { return JSON.parse(localStorage.getItem('adminUser') || '{}').email || 'Admin'; } catch { return 'Admin'; } })();

  // Requests awaiting a first reply — so a client's request can't sit unseen.
  const [reqCount, setReqCount] = useState(openReqs.n);
  useEffect(() => {
    if (!can('requests')) return;
    if (Date.now() - openReqs.at < OPEN_REQS_TTL) { setReqCount(openReqs.n); return; }
    (async () => {
      try {
        const token = await getFreshToken();
        const r = await fetch(`${API_BASE_URL}/auth/admin/service-requests/count`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!r.ok) return;
        const j = await r.json();
        if (typeof j.open === 'number') { openReqs = { n: j.open, at: Date.now() }; setReqCount(j.open); }
      } catch { /* a missing badge is not worth surfacing */ }
    })();
  }, []);

  const NavBtn = ({ path, icon: Icon, label, disabled, section, badge }: { path?: string; icon: LucideIcon; label: string; disabled?: boolean; section?: string; badge?: number }) => {
    if (section && !can(section)) return null;
    return (
      <button
        onClick={() => path && go(path)}
        disabled={disabled}
        title={disabled ? 'Coming soon' : undefined}
        className={`ax-side-btn${path && isActive(path) ? ' on' : ''}`}
      >
        <Icon />
        <span>{label}</span>
        {!!badge && <span className="ax-side-n">{badge > 99 ? '99+' : badge}</span>}
        {disabled && <span className="soon">Soon</span>}
      </button>
    );
  };

  const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <p className="ax-side-sec">{children}</p>
  );

  return (
    <>
      <button className="ax-side-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle navigation">
        {mobileMenuOpen ? <X /> : <Menu />}
      </button>

      {mobileMenuOpen && <div className="ax-side-ovl" onClick={() => setMobileMenuOpen(false)} />}

      <aside className={`ax-side${mobileMenuOpen ? ' open' : ''}`}>
        <div className="ax-side-head">
          <img src={logo} alt="Conncct" loading="lazy" />
          <span className="ax-side-badge"><i />{AX_ADMIN_LABEL}</span>
        </div>

        <nav className="ax-side-nav">
          <SectionLabel>Overview</SectionLabel>
          <NavBtn path={IS_CORP ? '/admin/accounting' : '/admin'} icon={TrendingUp} label={t('adminSidebar.dashboard')} />
          <NavBtn path="/admin/analytics" icon={BarChart3} label="Analytics" section="analytics" />
          <NavBtn path="/admin/leads" icon={UserCheck} label="Leads" section="leads" />
          <NavBtn path="/admin/requests" icon={Inbox} label="Requests" section="requests" badge={reqCount} />
          {/* Applications (inbound) and the curated roster (outbound) are two tabs of
              one page now — they looked like duplicate nav entries before. */}
          <NavBtn path="/admin/partners-hub" icon={Handshake} label="Partners" section="leads" />
          {!IS_CORP && <NavBtn path="/raising-request" icon={Users} label={t('adminSidebar.raisingRequest')} section="raising" />}

          {IS_CORP ? (<>
            {(can('services') || can('compliance')) && <SectionLabel>Services</SectionLabel>}
            <NavBtn path="/admin/services" icon={Briefcase} label="Services & Orders" section="services" />
            <NavBtn path="/admin/compliance-calendar" icon={CalendarDays} label="Compliance Calendar" section="compliance" />
            {can('pricing') && <SectionLabel>Compliance</SectionLabel>}
            <NavBtn path="/admin/command-center" icon={Command} label="Command Center" section="commandCenter" />
            <NavBtn path="/admin/pricing" icon={Tag} label="Accounting Pricing" section="pricing" />
            <NavBtn path="/admin/corporate-pricing" icon={Building2} label="Corporate Pricing" section="pricing" />
            <NavBtn path="/admin/setup-pricing" icon={Tag} label="Package Pricing" section="pricing" />
          </>) : (<>
            {(can('pricing') || can('content')) && <SectionLabel>Catalog</SectionLabel>}
            <NavBtn path="/admin/command-center" icon={Command} label="Command Center" section="commandCenter" />
            <NavBtn path="/admin/consulting-pricing" icon={Rocket} label="Consulting Pricing" section="pricing" />
            <NavBtn path="/admin/report-pricing" icon={Tag} label="Report Pricing" section="pricing" />
            <NavBtn path="/admin/content-studio" icon={FileEdit} label="Content Studio" section="content" />
            <NavBtn path="/admin/report-content" icon={ScrollText} label="Report Content" section="content" />
          </>)}

          <SectionLabel>System</SectionLabel>
          <NavBtn path="/admin/notifications" icon={Bell} label="Notifications" section="notifications" />
          <NavBtn path="/admin/blog" icon={PenLine} label="Blog" section="content" />
          <NavBtn path="/admin/vip" icon={Crown} label="VIP" section="team" />
          <NavBtn path="/admin/audit-log" icon={ShieldCheck} label="Audit Log" section="audit" />
          <NavBtn path="/admin-profile" icon={User} label={t('adminSidebar.adminProfile')} section="profile" />
          <NavBtn path="/send-invitation" icon={Send} label={t('adminSidebar.sendInvitation')} section="team" />
        </nav>

        <div className="ax-side-foot">
          <div className="ax-side-who">
            <div className="ax-side-av">{String(adminEmail)[0]?.toUpperCase() || 'A'}</div>
            <div>
              <b>{adminEmail}</b>
              <span>Administrator</span>
            </div>
          </div>
          <button onClick={handleLogout} className="ax-side-out">
            <LogOut />
            {t('adminSidebar.logout')}
          </button>
        </div>
      </aside>
    </>
  );
}
